import { useEffect, useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { apiClient } from '@/lib/api-client';

type VerificationStatus = 'verifying' | 'success' | 'error';

export function EmailVerificationPage() {
  const [searchParams] = useSearchParams();
  const [status, setStatus] = useState<VerificationStatus>('verifying');
  const [errorMessage, setErrorMessage] = useState<string>('');

  const token = searchParams.get('token');

  useEffect(() => {
    if (!token) {
      setStatus('error');
      setErrorMessage('Verification token is missing.');
      return;
    }

    const verifyEmail = async () => {
      try {
        await apiClient.post('/auth/verify-email', { token });
        setStatus('success');
      } catch {
        setStatus('error');
        setErrorMessage(
          'Email verification failed. The link may have expired or already been used.'
        );
      }
    };

    verifyEmail();
  }, [token]);

  return (
    <div className="flex min-h-screen items-center justify-center bg-atlas-canvas p-4">
      <div className="atlas-panel atlas-enter w-full max-w-md p-8 text-center">
        {status === 'verifying' && (
          <>
            <div
              className="mx-auto h-10 w-10 animate-spin rounded-full border-4 border-gray-200 border-t-primary-600"
              aria-label="Verifying email"
            />
            <h2 className="mt-4 text-xl font-semibold text-gray-900">
              Verifying your email…
            </h2>
            <p className="mt-2 text-sm text-gray-600">
              Please wait while we verify your email address.
            </p>
          </>
        )}

        {status === 'success' && (
          <>
            <svg
              className="mx-auto h-12 w-12 text-green-500"
              fill="none"
              viewBox="0 0 24 24"
              strokeWidth={1.5}
              stroke="currentColor"
              aria-hidden="true"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                d="M9 12.75L11.25 15 15 9.75M21 12a9 9 0 11-18 0 9 9 0 0118 0z"
              />
            </svg>
            <h2 className="mt-4 text-xl font-semibold text-gray-900">
              Email verified
            </h2>
            <p className="mt-2 text-sm text-gray-600">
              Your email has been verified successfully. You can now sign in to
              your account.
            </p>
            <Link
              to="/login"
              className="atlas-button-primary mt-6"
            >
              Sign in
            </Link>
          </>
        )}

        {status === 'error' && (
          <>
            <svg
              className="mx-auto h-12 w-12 text-red-500"
              fill="none"
              viewBox="0 0 24 24"
              strokeWidth={1.5}
              stroke="currentColor"
              aria-hidden="true"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                d="M12 9v3.75m9-.75a9 9 0 11-18 0 9 9 0 0118 0zm-9 3.75h.008v.008H12v-.008z"
              />
            </svg>
            <h2 className="mt-4 text-xl font-semibold text-gray-900">
              Verification failed
            </h2>
            <p role="alert" className="mt-2 text-sm text-red-600">
              {errorMessage}
            </p>
            <div className="mt-6 flex flex-col gap-2">
              <Link
                to="/signup"
                className="text-sm font-medium text-primary-600 hover:text-primary-800"
              >
                Sign up again
              </Link>
              <Link
                to="/login"
                className="text-sm font-medium text-gray-600 hover:text-gray-800"
              >
                Return to sign in
              </Link>
            </div>
          </>
        )}
      </div>
    </div>
  );
}
